import { useRef, useState, type FormEvent } from 'react';
import { numberHolder, studentFullName, type Student, type StudentFlag } from '@td/shared';
import {
  useCreateStudent,
  useUpdateStudent,
  useUploadStudentPhoto,
  useDeleteStudentPhoto,
} from '../hooks/useStudents';
import { useStudentFlags } from '../hooks/useStudentFlags';
import { StudentAvatar } from './StudentAvatar';
import { ApiRequestError } from '../lib/api';
import { Modal } from './Modal';

/** Add a student, or edit the one you're looking at. */
export function StudentEditor({
  student,
  roster,
  onManageFlags,
  onClose,
}: {
  student: Student | null;
  /** Everyone else, so a duplicate number can be caught before saving. */
  roster: Student[];
  onManageFlags: () => void;
  onClose: () => void;
}) {
  const create = useCreateStudent();
  const update = useUpdateStudent();
  const uploadPhoto = useUploadStudentPhoto();
  const deletePhoto = useDeleteStudentPhoto();
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: flagDefs = [] } = useStudentFlags();
  const available = flagDefs.filter((f) => !f.hidden);

  const [firstName, setFirstName] = useState(student?.firstName ?? '');
  const [lastName, setLastName] = useState(student?.lastName ?? '');
  const [preferredName, setPreferredName] = useState(student?.preferredName ?? '');
  const [flags, setFlags] = useState<StudentFlag[]>(student?.flags ?? []);
  const [about, setAbout] = useState(student?.about ?? '');
  const [number, setNumber] = useState(student?.number != null ? String(student.number) : '');
  const [error, setError] = useState<string | null>(null);

  // Checked as you type as well as on the server, so you find out before
  // filling in the rest of the form.
  const parsedNumber = Number(number);
  const clash =
    student?.active && number.trim() && Number.isInteger(parsedNumber)
      ? numberHolder(roster, parsedNumber, student.id)
      : null;

  const busy = create.isPending || update.isPending || uploadPhoto.isPending;

  function toggleFlag(f: StudentFlag) {
    setFlags((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const input = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      preferredName: preferredName.trim() || null,
      flags,
      about,
    };
    try {
      if (student) {
        // Archived students hold no number, so there is nothing to send.
        const wanted = student.active ? Number(number) : NaN;
        await update.mutateAsync({
          id: student.id,
          ...input,
          ...(Number.isInteger(wanted) && wanted !== student.number ? { number: wanted } : {}),
        });
      } else {
        await create.mutateAsync(input);
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That could not be saved.');
    }
  }

  async function onPickPhoto(file: File | undefined) {
    if (!file || !student) return;
    setError(null);
    try {
      await uploadPhoto.mutateAsync({ id: student.id, file });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That photo could not be saved.');
    }
  }

  return (
    <Modal
      onClose={onClose}
      label="Student"
      className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-4 text-lg">{student ? 'Edit student' : 'Add a student'}</h2>

      <form onSubmit={onSubmit} className="space-y-3">
        {student && (
          <div className="flex items-center gap-3">
            <StudentAvatar student={student} size={64} />
            <div className="flex flex-col gap-1 text-xs">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn-soft-accent"
                disabled={uploadPhoto.isPending}
              >
                {uploadPhoto.isPending
                  ? 'Uploading…'
                  : student.hasPhoto
                    ? 'Replace photo'
                    : 'Add a photo'}
              </button>
              {student.hasPhoto && (
                <button
                  type="button"
                  onClick={() => deletePhoto.mutate(student.id)}
                  className="btn-soft-danger"
                >
                  Remove photo
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="hidden"
                onChange={(e) => void onPickPhoto(e.target.files?.[0])}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="firstName">
              First name
            </label>
            <input
              id="firstName"
              className="field"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              maxLength={60}
              autoFocus
            />
          </div>
          <div>
            <label className="label" htmlFor="lastName">
              Last name
            </label>
            <input
              id="lastName"
              className="field"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              maxLength={60}
            />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="preferredName">
            Goes by <span className="font-normal text-stone">(if different)</span>
          </label>
          <input
            id="preferredName"
            className="field"
            value={preferredName}
            onChange={(e) => setPreferredName(e.target.value)}
            maxLength={60}
            placeholder={firstName || 'Nickname'}
          />
        </div>

        {student?.active && (
          <div>
            <label className="label" htmlFor="number">
              Class number
            </label>
            <input
              id="number"
              className="field w-24"
              type="number"
              min={1}
              max={999}
              value={number}
              onChange={(e) => setNumber(e.target.value)}
            />
            {clash && (
              <p className="mt-1 text-xs text-rust">
                {studentFullName(clash)} already has number {parsedNumber}.
              </p>
            )}
          </div>
        )}

        <fieldset>
          <div className="flex items-baseline justify-between">
            <legend className="label">Flags</legend>
            <button type="button" onClick={onManageFlags} className="btn-soft-accent mb-1.5">
              Edit flags
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {available.map((f) => {
              const on = flags.includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => toggleFlag(f.key)}
                  aria-pressed={on}
                  className="rounded-lg border px-2.5 py-1 text-xs transition-colors"
                  style={
                    on
                      ? { backgroundColor: f.color, borderColor: f.color, color: '#F7F3ED' }
                      : { borderColor: `${f.color}66`, color: f.color }
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div>
          <label className="label" htmlFor="about">
            About
          </label>
          <textarea
            id="about"
            className="field min-h-[80px]"
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            maxLength={4000}
            placeholder="Loves dinosaurs. Needs a quiet spot for tests. Sister in 4th."
          />
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-rose/15 px-3 py-2 text-sm text-rust">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy || !!clash}>
            {busy ? 'Saving…' : student ? 'Save' : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
